import React from 'react';
import { createPortal } from 'react-dom';
import {
  COMMUNICATION_CHOICE_COLORS,
  communicationQuestionToAnswerText,
  emptyCommunicationQuestion,
  type CommunicationChoiceOption,
  type CommunicationQuestion,
  type CommunicationQuestionType,
} from '../../utils/presentationCommunicationAnswers';

export type ProgramSlideCommunicationQuestionModalProps = {
  open: boolean;
  initialQuestion: CommunicationQuestion | null;
  initialMode?: 'edit' | 'view';
  /** true면 항상 보기모드로만 열리고 편집모드로 전환 불가(모니터링 모달 등) */
  readOnly?: boolean;
  saving?: boolean;
  /** 저장 실패 시 모달에 표시 (성공 시 부모에서 모달을 닫음) */
  saveError?: string | null;
  onClose: () => void;
  onSave: (
    question: CommunicationQuestion,
    promptText: string,
    answerText: string
  ) => void | Promise<void>;
};

function newChoiceId(): string {
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/** 과제관리 과제블럭(`AssignmentBlockCard` IconTrash)과 동일 */
const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ProgramSlideCommunicationQuestionModal: React.FC<
  ProgramSlideCommunicationQuestionModalProps
> = ({
  open,
  initialQuestion,
  initialMode = 'edit',
  readOnly = false,
  saving,
  saveError = null,
  onClose,
  onSave,
}) => {
  const [mode, setMode] = React.useState<'edit' | 'view'>(readOnly ? 'view' : initialMode);
  const [draft, setDraft] = React.useState<CommunicationQuestion>(() =>
    initialQuestion ?? emptyCommunicationQuestion('short')
  );

  React.useEffect(() => {
    if (!open) return;
    setMode(readOnly ? 'view' : initialMode);
    setDraft(initialQuestion ?? emptyCommunicationQuestion('short'));
  }, [open, initialMode, initialQuestion, readOnly]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setType = (type: CommunicationQuestionType) => {
    setDraft((prev) => {
      const next = emptyCommunicationQuestion(type);
      next.question = prev.question;
      if (type === 'choice' && prev.type === 'choice') {
        next.choices = prev.choices?.length ? prev.choices : next.choices;
        next.correctChoiceIndexes = prev.correctChoiceIndexes ?? [];
      }
      if (type === 'ox' && prev.type === 'ox') {
        next.correctChoiceIndexes = prev.correctChoiceIndexes ?? [];
      }
      if (type === 'short' && prev.type === 'short') {
        next.answers = prev.answers?.length ? prev.answers : [''];
      }
      if (type === 'essay' && prev.type === 'essay') {
        next.guide = prev.guide ?? '';
      }
      return next;
    });
  };

  const choices = draft.choices ?? [];
  const answers = draft.answers ?? [''];
  const correctSet = new Set(draft.correctChoiceIndexes ?? []);

  /** 단답·선택형은 정답이 있어야 저장 가능. 서술형은 질문 문구만 있으면 됨. */
  const canSave = (() => {
    if (!(draft.question || '').trim()) return false;
    if (draft.type === 'short') {
      return (draft.answers ?? []).some((a) => a.trim());
    }
    if (draft.type === 'choice') {
      const filled = (draft.choices ?? []).filter((c) => c.text.trim());
      if (filled.length < 2) return false;
      return (draft.correctChoiceIndexes ?? []).length > 0;
    }
    return true;
  })();

  const handleSave = () => {
    if (!canSave || saving) return;
    let cleaned: CommunicationQuestion = { ...draft, question: draft.question.trim() };
    if (cleaned.type === 'short') {
      cleaned = {
        ...cleaned,
        answers: (cleaned.answers ?? []).map((a) => a.trim()).filter(Boolean),
      };
    }
    if (cleaned.type === 'choice') {
      const nextChoices = (cleaned.choices ?? [])
        .map((c) => ({ ...c, text: c.text.trim() }))
        .filter((c) => c.text);
      const indexMap = new Map<number, number>();
      (cleaned.choices ?? []).forEach((c, oldIdx) => {
        const ni = nextChoices.findIndex((x) => x.id === c.id);
        if (ni >= 0) indexMap.set(oldIdx, ni);
      });
      cleaned = {
        ...cleaned,
        choices: nextChoices,
        correctChoiceIndexes: (cleaned.correctChoiceIndexes ?? [])
          .map((i) => indexMap.get(i))
          .filter((i): i is number => i != null),
      };
    }
    if (cleaned.type === 'essay') {
      cleaned = { ...cleaned, guide: (cleaned.guide ?? '').trim() };
    }
    onSave(cleaned, cleaned.question, communicationQuestionToAnswerText(cleaned));
  };

  const toggleCorrect = (index: number) => {
    setDraft((prev) => {
      const set = new Set(prev.correctChoiceIndexes ?? []);
      if (set.has(index)) set.delete(index);
      else set.add(index);
      return { ...prev, correctChoiceIndexes: Array.from(set).sort((a, b) => a - b) };
    });
  };

  const updateChoiceText = (id: string, text: string) => {
    setDraft((prev) => ({
      ...prev,
      choices: (prev.choices ?? []).map((c) => (c.id === id ? { ...c, text } : c)),
    }));
  };

  const removeChoice = (id: string) => {
    setDraft((prev) => {
      const list = prev.choices ?? [];
      const removedIdx = list.findIndex((c) => c.id === id);
      const next = list.filter((c) => c.id !== id);
      const correctChoiceIndexes = (prev.correctChoiceIndexes ?? [])
        .filter((i) => i !== removedIdx)
        .map((i) => (i > removedIdx ? i - 1 : i));
      return { ...prev, choices: next, correctChoiceIndexes };
    });
  };

  const addChoice = () => {
    setDraft((prev) => ({
      ...prev,
      choices: [...(prev.choices ?? []), { id: newChoiceId(), text: '' } satisfies CommunicationChoiceOption],
    }));
  };

  const modal = (
    <div
      className="cp-comm-q-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="cp-comm-q-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-comm-q-modal-title"
        data-tutorial-slide="question-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-comm-q-modal-header">
          <h2 id="cp-comm-q-modal-title" className="cp-comm-q-modal-title">
            문제 상세
          </h2>
          {readOnly ? (
            <span className="cp-comm-q-modal-mode-readonly" title="모니터링 화면에서는 조회만 가능합니다">
              보기모드
            </span>
          ) : (
            <div className="cp-comm-q-modal-mode-tabs" role="tablist" aria-label="모드">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'edit'}
                className={'cp-comm-q-modal-mode-tab' + (mode === 'edit' ? ' is-active' : '')}
                onClick={() => setMode('edit')}
              >
                편집모드
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'view'}
                className={'cp-comm-q-modal-mode-tab' + (mode === 'view' ? ' is-active' : '')}
                onClick={() => setMode('view')}
              >
                보기모드
              </button>
            </div>
          )}
          <button
            type="button"
            className="cp-comm-q-modal-close"
            data-tutorial-slide="question-detail-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </header>

        <div
          className={
            'cp-comm-q-modal-body' + (mode === 'view' ? ' is-view' : ' is-edit')
          }
        >
          {mode === 'view' ? (
            <div className="cp-comm-q-view">
              <p className="cp-comm-q-view-question">
                {(draft.question || '').trim() || '질문이 없습니다.'}
              </p>
              {draft.type === 'choice' && (draft.choices ?? []).some((c) => c.text.trim()) ? (
                <ul className="cp-comm-q-view-choices">
                  {(draft.choices ?? [])
                    .map((c, index) => ({ c, index }))
                    .filter(({ c }) => c.text.trim())
                    .map(({ c, index }, displayIndex) => {
                      const isCorrect = readOnly && correctSet.has(index);
                      return (
                        <li
                          key={c.id}
                          className={
                            'cp-comm-q-view-choice' + (isCorrect ? ' is-correct' : '')
                          }
                        >
                          <span
                            className="cp-comm-q-view-choice-num"
                            style={{
                              background: COMMUNICATION_CHOICE_COLORS[index % COMMUNICATION_CHOICE_COLORS.length],
                            }}
                          >
                            {displayIndex + 1}번
                          </span>
                          <span className="cp-comm-q-view-choice-text">{c.text}</span>
                          {isCorrect ? <span className="cp-comm-q-view-choice-correct-tag">정답</span> : null}
                        </li>
                      );
                    })}
                </ul>
              ) : null}
              {draft.type === 'ox' ? (
                <div className="cp-comm-q-ox-row" aria-label="O 또는 X">
                  {(['O', 'X'] as const).map((l, i) => (
                    <span
                      key={l}
                      className={`cp-comm-q-type-btn cp-comm-q-ox-btn cp-comm-q-ox-btn--${i === 0 ? 'o' : 'x'}` + (readOnly && correctSet.has(i) ? ' is-active' : '')}
                    >
                      {l}
                      {readOnly && correctSet.has(i) ? <span className="cp-comm-q-view-choice-correct-tag">정답</span> : null}
                    </span>
                  ))}
                </div>
              ) : null}
              {draft.type === 'essay' && (draft.guide || '').trim() ? (
                <p className="cp-comm-q-view-guide">{draft.guide}</p>
              ) : null}
              {draft.type === 'short' ? (
                readOnly ? (
                  <p className="cp-comm-q-view-answer">
                    정답: {(draft.answers ?? []).filter((a) => a.trim()).join(' / ') || '설정된 정답이 없습니다.'}
                  </p>
                ) : (
                  <p className="cp-comm-q-view-hint">단답 문제 · 정답은 표시되지 않습니다.</p>
                )
              ) : null}
            </div>
          ) : (
            <div className="cp-comm-q-edit">
              <div className="cp-comm-q-type-row" role="group" aria-label="문제 유형">
                {(
                  [
                    { id: 'short', label: '단답', tutorial: 'question-type-short' },
                    { id: 'ox', label: 'OX', tutorial: 'question-type-ox' },
                    { id: 'choice', label: '선택형', tutorial: 'question-type-choice' },
                    { id: 'essay', label: '서술형', tutorial: 'question-type-essay' },
                  ] as const
                ).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={
                      'cp-comm-q-type-btn' + (draft.type === t.id ? ' is-active' : '')
                    }
                    data-tutorial-slide={t.tutorial}
                    onClick={() => setType(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <input
                type="text"
                className="cp-comm-q-question-input"
                value={draft.question}
                onChange={(e) => setDraft((p) => ({ ...p, question: e.target.value }))}
                placeholder="질문을 입력하세요..."
                aria-label="질문"
              />

              {draft.type === 'choice' ? (
                <div className="cp-comm-q-choice-list">
                  {choices.map((c, index) => {
                    const isCorrect = correctSet.has(index);
                    return (
                      <div key={c.id} className="cp-comm-q-choice-row">
                        <button
                          type="button"
                          className={
                            'cp-comm-q-choice-num' + (isCorrect ? ' is-correct' : '')
                          }
                          data-tutorial-slide="question-choice-correct"
                          style={{ background: COMMUNICATION_CHOICE_COLORS[index % COMMUNICATION_CHOICE_COLORS.length] }}
                          title={isCorrect ? '정답 해제' : '정답으로 표시'}
                          aria-pressed={isCorrect}
                          onClick={() => toggleCorrect(index)}
                        >
                          {isCorrect ? (
                            <span className="cp-comm-q-choice-correct-tip">정답</span>
                          ) : null}
                          {index + 1}
                        </button>
                        <div className="cp-comm-q-choice-input-wrap">
                          <input
                            type="text"
                            className="cp-comm-q-choice-input"
                            value={c.text}
                            onChange={(e) => updateChoiceText(c.id, e.target.value)}
                            placeholder={`${index + 1}번 선택지 내용을 입력하세요...`}
                          />
                          <button
                            type="button"
                            className="cp-comm-q-icon-btn"
                            aria-label="선택지 삭제"
                            onClick={() => removeChoice(c.id)}
                            disabled={choices.length <= 2}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <button type="button" className="cp-comm-q-add-dashed" onClick={addChoice}>
                    +
                  </button>
                </div>
              ) : null}

              {draft.type === 'ox' ? (
                <div className="cp-comm-q-ox-row" role="group" aria-label="정답 (선택 안 하면 정답 없음)">
                  {(['O', 'X'] as const).map((l, i) => {
                    const on = correctSet.has(i);
                    return (
                      <button
                        key={l}
                        type="button"
                        className={`cp-comm-q-type-btn cp-comm-q-ox-btn cp-comm-q-ox-btn--${i === 0 ? 'o' : 'x'}` + (on ? ' is-active' : '')}
                        aria-pressed={on}
                        title={on ? '정답 해제' : `${l}를 정답으로`}
                        onClick={() => setDraft((prev) => ({ ...prev, correctChoiceIndexes: on ? [] : [i] }))}
                      >
                        {l}
                        {on ? <span className="cp-comm-q-choice-correct-tip">정답</span> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {draft.type === 'short' ? (
                <div className="cp-comm-q-answer-list">
                  {answers.map((ans, index) => (
                    <div key={`ans-${index}`} className="cp-comm-q-choice-input-wrap">
                      <input
                        type="text"
                        className="cp-comm-q-choice-input"
                        value={ans}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraft((prev) => {
                            const next = [...(prev.answers ?? [''])];
                            next[index] = v;
                            return { ...prev, answers: next };
                          });
                        }}
                        placeholder="정답을 입력하세요"
                      />
                      <button
                        type="button"
                        className="cp-comm-q-icon-btn"
                        aria-label="정답 삭제"
                        disabled={answers.length <= 1}
                        onClick={() => {
                          setDraft((prev) => {
                            const next = (prev.answers ?? ['']).filter((_, i) => i !== index);
                            return { ...prev, answers: next.length ? next : [''] };
                          });
                        }}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="cp-comm-q-add-dashed is-label"
                    data-tutorial-slide="question-short-add-answer"
                    onClick={() =>
                      setDraft((prev) => ({
                        ...prev,
                        answers: [...(prev.answers ?? ['']), ''],
                      }))
                    }
                  >
                    +정답 추가
                  </button>
                </div>
              ) : null}

              {draft.type === 'essay' ? (
                <textarea
                  className="cp-comm-q-guide-input"
                  data-tutorial-slide="question-essay-guide"
                  value={draft.guide ?? ''}
                  onChange={(e) => setDraft((p) => ({ ...p, guide: e.target.value }))}
                  placeholder="구체적인 안내 메시지를 입력하세요..."
                  rows={5}
                />
              ) : null}

              {saveError ? (
                <p className="cp-comm-q-save-error" role="alert">
                  {saveError}
                </p>
              ) : null}
              <div className="cp-comm-q-edit-actions">
                <button
                  type="button"
                  className="cp-comm-q-btn-cancel"
                  data-tutorial-slide="question-detail-cancel"
                  onClick={onClose}
                  disabled={!!saving}
                >
                  취소하기
                </button>
                <button
                  type="button"
                  className="cp-comm-q-btn-save"
                  disabled={!canSave || !!saving}
                  onClick={handleSave}
                  title={
                    canSave
                      ? '질문에 반영합니다. 페이지 저장하기로 DB에 저장하세요.'
                      : draft.type === 'short'
                        ? '질문과 정답을 입력해야 저장할 수 있어요'
                        : draft.type === 'choice'
                          ? '질문·선택지(2개 이상)·정답 표시가 있어야 저장할 수 있어요'
                          : '질문 문구를 입력해야 저장할 수 있어요'
                  }
                >
                  {saving ? '저장 중…' : '저장하기'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ProgramSlideCommunicationQuestionModal;
